import { Injectable, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LocationService, ParamsService } from '@callrail/looky/util';
import { filter } from 'rxjs';
import { CurrentContextService } from '../current-context/current-context.service';
import { CognitoCookieService } from '../cognito-cookie/cognito-cookie.service';

const newSupportRequestPath =
  'https://support.callrail.com/hc/en-us/requests/new';

@Injectable({
  providedIn: 'root',
})
export class RoutesService {
  public routes: {
    [key: string]: (params?: any, addQueryParams?: boolean) => string;
  } = {};

  constructor(
    private context: CurrentContextService,
    private paramsService: ParamsService,
    private locationService: LocationService,
    public cognitoCookieService: CognitoCookieService,
    @Optional() private router: Router
  ) {
    this.initialize(this.getRoutesFromBody() || {});
  }

  public initialize(routes: any) {
    const hardCoded = { new_support_request_path: () => newSupportRequestPath };

    this.routes = Object.keys(routes).reduce((routeFns, key) => {
      routeFns[key] = this.paramInjectorFactory(routes[key]);
      return routeFns;
    }, hardCoded);
  }

  public withContext(routeName): string {
    const resolved = this.routes[routeName](
      {
        agency_id: this.context.currentAgency.id,
        company_id: this.context.currentCompany
          ? this.context.currentCompany.id
          : null,
        user_id: this.context.currentUser ? this.context.currentUser.id : null,
      },
      false
    );

    if (resolved.includes(':')) {
      throw Error('Context insufficient to resolve route: ' + routeName);
    }
    return resolved;
  }

  public saveLastVisitedUrlOnNavigationEnd() {
    this.router?.events
      ?.pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const url = this.locationService.absPath();
        if (!url.includes('/mfa-migration')) {
          this.cognitoCookieService.lastVisitedUrl =
            this.locationService.absPath();
        }
      });
  }

  private paramInjectorFactory(
    urlPattern: string
  ): (params?: any, addQueryParams?: boolean) => string {
    return (params: any = {}, addQueryParams: boolean = true) =>
      this.injectParams(urlPattern, params, addQueryParams);
  }

  private injectParams(
    urlPattern: string,
    params: any,
    addQueryParams: boolean
  ): string {
    let route = urlPattern;

    const paramsToInject = Object.assign({}, params);

    // inject params that have symbols in the url
    Object.keys(paramsToInject).forEach((key) => {
      if (route.includes(':' + key)) {
        route = route.replace(':' + key, paramsToInject[key]);
        delete paramsToInject[key];
      }
    });

    if (addQueryParams) {
      // then make query string parameters for the rest
      route = this.paramsService.appendToString(route, paramsToInject);
    }

    return route;
  }

  private getRoutesFromBody() {
    const bodyRoutes = JSON.parse(
      document.body.getAttribute('data-ng-rails-routes')
    );
    document
      .getElementsByTagName('body')[0]
      .removeAttribute('data-ng-rails-routes');
    return bodyRoutes;
  }
}
